import React, { useEffect, useMemo, useState } from "react";
import { Container, Table, Alert, Button, Spinner, Modal, Form } from "react-bootstrap";
import API from "../../services/api";
import "../EmployeeAccounts.css";

export const orDash = (v) => (v === 0 ? "0" : v == null || String(v).trim() === "" ? "—" : v);
export const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");

export const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const titleIcon = {
  Payroll: "group",
  "GST / TDS": "gavel",
  "Financial Reports": "bar_chart",
  "Audit Logs": "history",
};

const AccountsListPage = ({
  title,
  subtitle,
  endpoint,
  columns,
  createFields = [],
  createPerms = [],
  approvePerms = [],
  statusField = "status",
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get(endpoint);
      setRows(normalizeRows(res.data));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadRows = async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await API.get(endpoint);
        setRows(normalizeRows(res.data));
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.message || "Failed to load data");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    loadRows();
  }, [endpoint]);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
  }, []);

  const canCreate = createPerms.length === 0 || createPerms.some((p) => perms.includes(p));
  const canApprove = approvePerms.length === 0 || approvePerms.some((p) => perms.includes(p));

  const openCreate = () => {
    const init = {};
    createFields.forEach((f) => {
      init[f.key] = "";
    });
    setForm(init);
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post(endpoint, form);
      setShowCreate(false);
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Create failed");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await API.patch(`${endpoint}/${id}/status`, { status });
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Update failed");
    }
  };

  const searchText = search.trim().toLowerCase();
  const statusOptions = useMemo(() => {
    const values = rows
      .map((row) => row?.[statusField])
      .filter((value) => typeof value === "string" && value.trim());
    return [...new Set(values)];
  }, [rows, statusField]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesStatus =
        !statusFilter || String(row?.[statusField] || "").toLowerCase() === statusFilter.toLowerCase();
      const matchesSearch =
        !searchText ||
        columns.some((column) => {
          const raw = column.render ? "" : row?.[column.key];
          return String(raw || "").toLowerCase().includes(searchText);
        }) ||
        JSON.stringify(row || {}).toLowerCase().includes(searchText);
      return matchesStatus && matchesSearch;
    });
  }, [columns, rows, searchText, statusField, statusFilter]);

  const approvedCount = rows.filter((row) =>
    String(row?.[statusField] || "").toLowerCase().includes("approved")
  ).length;
  const pendingCount = rows.filter((row) =>
    String(row?.[statusField] || "").toLowerCase().includes("pending")
  ).length;

  const tableColumns = [...columns];
  if (canApprove) {
    tableColumns.push({
      key: "_actions",
      label: "Action",
      align: "right",
      render: (r) => (
        <div className="accounts-module-actions">
          <button type="button" className="approve" onClick={() => updateStatus(r._id, "approved")}>
            Approve
          </button>
          <button type="button" className="reject" onClick={() => updateStatus(r._id, "rejected")}>
            Reject
          </button>
        </div>
      ),
    });
  }

  return (
    <Container className="accounts-page accounts-module-page">
      <section className="accounts-module-shell">
        <header className="accounts-module-header">
          <div className="accounts-module-title">
            <div className="accounts-module-icon">
              <span className="material-symbols-outlined">{titleIcon[title] || "account_balance"}</span>
            </div>
            <div>
              <h2>{title}</h2>
              <p>{subtitle}</p>
            </div>
          </div>
          <div className="accounts-module-header-actions">
            <Button variant="outline-secondary" onClick={fetchRows}>
              Refresh
            </Button>
            {canCreate && createFields.length ? (
              <Button className="accounts-btn-primary" onClick={openCreate}>
                Add New
              </Button>
            ) : null}
          </div>
        </header>

        <section className="accounts-module-summary">
          <article>
            <span>Total Records</span>
            <strong>{rows.length}</strong>
          </article>
          <article>
            <span>Visible Rows</span>
            <strong>{filteredRows.length}</strong>
          </article>
          <article>
            <span>Approved</span>
            <strong>{approvedCount}</strong>
          </article>
          <article>
            <span>Pending</span>
            <strong>{pendingCount}</strong>
          </article>
        </section>

        <section className="accounts-module-filters">
          <label className="accounts-module-search">
            <span className="material-symbols-outlined">search</span>
            <input
              placeholder={`Search ${title.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Form.Select>
        </section>

        {err && <Alert variant="danger" className="mx-4 mb-0">{err}</Alert>}

        <section className="accounts-module-tablecard">
          {loading ? (
            <div className="accounts-module-loading">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="accounts-module-tablewrap">
              <Table responsive className="accounts-module-table">
                <thead>
                  <tr>
                    {tableColumns.map((c) => (
                      <th key={c.key} className={c.align === "right" ? "text-end" : ""}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumns.length} className="text-center py-5 text-muted">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r, i) => (
                      <tr key={r._id || r.id || i}>
                        {tableColumns.map((c) => (
                          <td key={c.key} className={c.align === "right" ? "text-end" : ""}>
                            {c.render ? c.render(r) : orDash(r[c.key])}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </section>
      </section>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} fullscreen scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Add {title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitCreate}>
            {createFields.map((f) => (
              <Form.Group className="mb-3" key={f.key}>
                <Form.Label>{f.label}</Form.Label>
                <Form.Control
                  type={f.type || "text"}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  required={!!f.required}
                />
              </Form.Group>
            ))}
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" className="accounts-btn-primary">
                Save
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AccountsListPage;
